/**
 * Hook to initialize organization context for popup/focus windows in Tauri.
 *
 * In Tauri, popup windows have isolated JavaScript contexts, meaning Zustand stores
 * don't share state with the main window. This hook ensures the organization is
 * properly initialized from localStorage (which IS shared across windows).
 *
 * This is critical for features like note editing that depend on organization context
 * for socket room membership and realtime updates.
 */

import { useEffect, useRef } from 'react';
import { useOrganizationStore } from '@/store/organization.store';
import { useOrganizationsQuery } from '@/hooks/queries/useOrganizationsQuery';
import { useMyHostOrgsQuery } from '@/hooks/queries/useExternalOrgsQuery';
import { useAuth } from '@/store/auth.store';
import { createLogger } from '@/lib/logger';
import { extractOrgSlug } from '@/utils/org-paths';
import { useLocation } from 'react-router-dom';

const logger = createLogger('hooks/usePopupOrganizationInit');

/**
 * Initializes organization context from localStorage for popup windows.
 * This should be called early in the app initialization, after auth is ready.
 * Uses React Query hooks to leverage caching and deduplication.
 */
export function usePopupOrganizationInit() {
  const { pathname } = useLocation();
  const isClientPortalRoute = pathname.startsWith('/client-portal');
  const { user, isLoading: isAuthLoading, isAuthenticated } = useAuth();
  const { currentOrganization, setCurrentOrganization } = useOrganizationStore();
  const hasInitialized = useRef(false);
  const storedOrgId =
    typeof window !== 'undefined' && !isClientPortalRoute
      ? localStorage.getItem('currentOrganizationId')
      : null;

  // Resolve direct memberships first. Most popup windows are for the active org,
  // so this avoids paying for the cross-org host lookup on every new window.
  const { data: organizations = [], isLoading: isOrgsLoading } = useOrganizationsQuery({
    enabled:
      isAuthenticated &&
      !currentOrganization &&
      !isClientPortalRoute &&
      !!storedOrgId,
  });

  const directOrg = storedOrgId
    ? organizations.find(org => org.id === storedOrgId)
    : undefined;
  const hasDirectMembership = !!directOrg?.userMembership;
  const shouldLookupExternalOrg =
    isAuthenticated &&
    !currentOrganization &&
    !isClientPortalRoute &&
    !!storedOrgId &&
    !isOrgsLoading &&
    (!directOrg || !hasDirectMembership);

  // Only fetch cross-org relationships when the active org isn't one of the
  // user's direct memberships.
  const { data: externalOrgs = [], isLoading: isExternalOrgsLoading } =
    useMyHostOrgsQuery({
      enabled: shouldLookupExternalOrg,
    });

  useEffect(() => {
    // Never initialize regular-app org context while on client portal routes.
    // This avoids org-store resets and socket reconnect churn in portal views.
    if (isClientPortalRoute) {
      return;
    }

    // Skip if already initialized
    if (hasInitialized.current) {
      return;
    }

    // Wait for auth to be ready
    if (isAuthLoading) {
      return;
    }

    // Skip if no user (not authenticated)
    if (!user) {
      return;
    }

    // Skip if organization is already set (main window or already initialized)
    if (currentOrganization) {
      hasInitialized.current = true;
      return;
    }

    // Check if we have an organization ID in localStorage
    if (!storedOrgId) {
      logger.debug('[PopupOrgInit] No organization ID in localStorage, skipping');
      hasInitialized.current = true;
      return;
    }

    // Wait for direct memberships first.
    if (isOrgsLoading) {
      return;
    }

    // A URL that names one of the user's organizations is resolved from the URL
    // by OrgRouteGuard. Priming from localStorage there installs a *different*
    // org for a few renders, which is long enough for org-scoped hooks to
    // answer for the wrong org (a past-due stored org bounced a healthy org to
    // its subscription-setup page). Slug-less windows — the tray popover, the
    // companion, settings/help routes — never match an org slug, so they still
    // get the stored org.
    const urlOrgSlug = extractOrgSlug(pathname);
    if (urlOrgSlug && organizations.some(org => org.slug === urlOrgSlug)) {
      logger.debug(
        `[PopupOrgInit] URL owns the organization (${urlOrgSlug}), leaving it to OrgRouteGuard`,
      );
      return;
    }

    logger.info(`[PopupOrgInit] Initializing organization from localStorage: ${storedOrgId}`);

    if (directOrg && hasDirectMembership) {
      logger.info(`[PopupOrgInit] Found direct organization with membership: ${directOrg.name}, setting in store`);
      setCurrentOrganization(directOrg);
      hasInitialized.current = true;
      return;
    }

    if (shouldLookupExternalOrg && isExternalOrgsLoading) {
      return;
    }

    // Not found in direct orgs OR found but without membership - check external orgs
    logger.info(`[PopupOrgInit] Organization ${storedOrgId} not found in direct orgs (or no membership), checking external orgs...`);

    const externalOrgRelationship = externalOrgs.find(
      rel => rel.hostOrganization?.id === storedOrgId
    );

    if (externalOrgRelationship?.hostOrganization) {
      const hostOrg = externalOrgRelationship.hostOrganization;
      logger.info(`[PopupOrgInit] Found external organization: ${hostOrg.name}, setting in store`);

      // Set as external org with the relationship for permission checking
      setCurrentOrganization(
        {
          id: hostOrg.id,
          name: hostOrg.name,
          slug: hostOrg.slug,
          logo: hostOrg.logo || null,
          createdAt: new Date(),
          updatedAt: new Date(),
          isExternalAccess: true,
        },
        externalOrgRelationship
      );
    } else {
      logger.warn(`[PopupOrgInit] Organization ${storedOrgId} not found in direct or external orgs`);
      // Don't clear localStorage here - let OrgRouteGuard handle the redirect
    }

    hasInitialized.current = true;
  }, [
    user,
    isAuthLoading,
    currentOrganization,
    setCurrentOrganization,
    organizations,
    externalOrgs,
    isOrgsLoading,
    isExternalOrgsLoading,
    isClientPortalRoute,
    pathname,
    directOrg,
    hasDirectMembership,
    shouldLookupExternalOrg,
    storedOrgId,
  ]);
}

/**
 * Component version of the hook for use in the provider tree
 */
export function PopupOrganizationInitializer() {
  usePopupOrganizationInit();
  return null;
}
